import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BootstrapService } from 'app/service/bootstrap.service';
import { UserService } from 'app/service/user.service';
import { CookieService } from 'ngx-cookie-service';
import { checkPasswordRules, translatePasswordErrors } from 'app/validator/password-rules';
import { environment } from 'environment/environment';

type PageState = 'loading' | 'ready' | 'invalid';

@Component({
  standalone: false,
  selector: 'app-accept',
  templateUrl: './accept.component.html',
  styleUrls: ['./accept.component.scss']
})
export class AcceptComponent implements OnInit {
  readonly currentYear = new Date().getFullYear();

  state: PageState = 'loading';
  form: FormGroup;
  submitted = false;
  isLoading = false;
  showPassword = false;
  showRepeat = false;
  serverError = '';

  uuid = '';
  invitation: any = null;
  selectedLanguage = 'en';

  supportedLanguages: { code: string; name: string }[] = [
    { code: 'de', name: 'Deutsch' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'pt', name: 'Português' }
  ];

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private userService: UserService,
    private bootstrapService: BootstrapService,
    private cookieService: CookieService
  ) {
    translate.setDefaultLang('en');
  }

  ngOnInit() {
    const stored = localStorage.getItem('selectedLanguage');
    this.selectedLanguage = this.supportedLanguages.some(l => l.code === stored) ? stored as string : 'en';
    localStorage.setItem('selectedLanguage', this.selectedLanguage);
    this.translate.use(this.selectedLanguage);

    this.form = this.formBuilder.group({
      email: [{ value: '', disabled: true }],
      password: ['', Validators.required],
      repeatpass: ['', Validators.required],
      language: [this.selectedLanguage, Validators.required],
      terms: [false, Validators.requiredTrue],
      consent: [false]
    });

    this.uuid = this.parseUuid(this.route.snapshot.queryParams['uuid']);
    if (!this.uuid) {
      this.state = 'invalid';
      return;
    }
    this.userService.getInvitation(this.uuid).subscribe(
      invitation => {
        this.invitation = invitation;
        this.form.patchValue({ email: invitation.email });
        // Anwendende (Manager) stimmen nur den Nutzungsbedingungen zu, Teilnehmende geben zusätzlich die Einwilligung
        if (this.isParticipant) {
          this.form.get('consent')?.setValidators(Validators.requiredTrue);
          this.form.get('consent')?.updateValueAndValidity();
        }
        this.state = 'ready';
      },
      () => (this.state = 'invalid')
    );
  }

  // Ältere Einladungs-Mails enthalten "…uuid=<Platzhalter>?uuid=<echte UUID>"
  private parseUuid(raw: string | undefined): string {
    if (!raw) {
      return '';
    }
    const parts = raw.split('?uuid=');
    return parts[parts.length - 1];
  }

  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  get isParticipant(): boolean {
    return ['EMPLOYEE', 'APPLICANT', 'TRAINEE'].includes(this.invitation?.role);
  }

  get consentsGiven(): boolean {
    return this.form.get('terms')?.value === true && (!this.isParticipant || this.form.get('consent')?.value === true);
  }

  get fullName(): string {
    return this.invitation ? `${this.invitation.first_name} ${this.invitation.last_name}`.trim() : '';
  }

  get password(): string {
    return this.form.get('password')?.value || '';
  }

  // Nutzerdaten für die Ähnlichkeitsregel (wie im Backend)
  get similarTo(): string[] {
    return this.invitation ? [this.invitation.email, this.invitation.first_name, this.invitation.last_name] : [];
  }

  get rulesMet(): boolean {
    return checkPasswordRules(this.password, this.similarTo).met;
  }

  get repeatMismatch(): boolean {
    return this.form.get('repeatpass')?.value !== this.password;
  }

  changeLanguage(lang: string) {
    this.selectedLanguage = lang;
    this.translate.use(lang);
    localStorage.setItem('selectedLanguage', lang);
    this.userService.loadLang.next(lang);
    this.form.patchValue({ language: lang }, { emitEvent: false });
  }

  join() {
    this.submitted = true;
    this.serverError = '';
    if (this.form.invalid || !this.rulesMet || this.repeatMismatch || this.isLoading) {
      return;
    }
    this.isLoading = true;

    const data = {
      password: this.password,
      terms_accepted: this.form.get('terms')?.value === true,
      consent_accepted: this.form.get('consent')?.value === true,
      language: this.form.get('language')?.value.toUpperCase()
    };
    this.userService.acceptInvitation(this.uuid, data).subscribe(
      res => this.autoLogin(res.accessToken),
      error => {
        this.isLoading = false;
        const body = error?.error;
        const passwordErrors = Array.isArray(body?.password) ? translatePasswordErrors(this.translate, body.password, body.password_codes) : [];
        this.serverError = passwordErrors.length ? passwordErrors.join(' ') : this.translate.instant('acceptPage.errorGeneric');
      }
    );
  }

  // Wie LoginComponent.login(): Token und Rolle als Cookie, danach direkt zum Fragebogen
  private autoLogin(accessToken: string) {
    this.cookieService.set('authToken', accessToken, { path: '/', secure: true, sameSite: 'Strict' });
    this.userService.getMe().subscribe(
      (me: any) => {
        this.cookieService.set('userRole', me.role, { path: '/', secure: true, sameSite: 'Strict' });
        localStorage.setItem('candidateId', me.uuid);
        localStorage.setItem('companyId', me.company);
        if (me.role === 'EMPLOYEE' || me.role === 'APPLICANT') {
          this.userService.setSelectedReport(me.uuid);
          this.userService.setSelectedEmployee(me);
        }
        this.isLoading = false;
        if (me.role === 'ADMIN') {
          this.bootstrapService.bootstrap();
        } else {
          this.router.navigate(['/questionary']);
        }
      },
      () => {
        // Konto ist angelegt, nur der Auto-Login ist fehlgeschlagen
        this.isLoading = false;
        this.router.navigate(['/login']);
      }
    );
  }
}
